import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Checkbox, Icon, Popup } from "semantic-ui-react";

import { ReactTable } from "components";
import { useShowPriceInVat } from "hooks";
import "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOfferModal/components/TyreExternalOfferTable/TyreExternalOfferTable.scss";
import { getFullTyreDescription } from "modules/AppointmentDetails/components/VehicleTyres/utils";
import { getTyreSeasonIcon, roundTwoDecimals } from "utils/common";
import { formatPrice } from "utils/formatPrice";

import type { TYRETEAM_CHANNELS } from "models";
import type { ReplacementOffer } from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOfferModal/hooks";

type TyreExternalOfferTableProps = {
  replacementOffers: ReplacementOffer[];
  is_tyre_net_enabled: boolean | undefined;
  getDisplayedOfferPrice: (price: number) => number;
  onSelectionUpdate: (offer: ReplacementOffer, checked: boolean) => void;
  getDeliveryPopup: (delivery?: string) => { content: string; trigger: string } | null;
  getDeliveryChannelName: (channel?: TYRETEAM_CHANNELS) => string;
};

const columnHelper = createColumnHelper<ReplacementOffer>();

const getGrossPrice = (item: ReplacementOffer) => {
  if (!(item.stock && item.gross_price)) return "0.0";
  return `${formatPrice(item.gross_price)}${item.discount ? ` - ${item.discount}%` : ""}`;
};

const getNetPrice = (item: ReplacementOffer) => {
  if (!item.stock || !item.net_price) return "0.0";
  return `${formatPrice(item.net_price)}${item.net_margin_percent ? ` + ${item.net_margin_percent}%` : item.net_margin_euro ? ` + ${item.net_margin_euro}€` : ""}`;
};

export const TyreExternalOfferTable = ({
  replacementOffers,
  is_tyre_net_enabled,
  getDisplayedOfferPrice,
  onSelectionUpdate,
  getDeliveryChannelName,
  getDeliveryPopup
}: TyreExternalOfferTableProps) => {
  const { t } = useTranslation();

  const showPriceInVat = useShowPriceInVat();

  const renderDeliveryPopup = (delivery?: string) => {
    const type = getDeliveryPopup(delivery);
    return type ? <Popup content={<span>{type.content}</span>} trigger={<span>{type.trigger}</span>} /> : null;
  };

  const columns = [
    columnHelper.accessor(row => row, {
      id: "season",
      header: "",
      size: 45,
      cell: ({ row: { original } }) => (
        <>
          <Icon className={getTyreSeasonIcon(original.season)} />
          {original.three_peak_mountain_snow_flake && (
            <div className="three-pmsf-icon">
              <img src="/images/3pmsf_outline_white.svg" alt="3pmsf" />
            </div>
          )}
        </>
      )
    }),
    columnHelper.accessor(row => row, {
      id: "description",
      header: () => t("v8_description", "Description"),
      size: 330,
      cell: ({ row: { original } }) => getFullTyreDescription(original, is_tyre_net_enabled)
    }),
    columnHelper.accessor(row => row, {
      id: "brand",
      header: () => t("v8_brand", "Brand"),
      size: 120,
      cell: ({ row: { original } }) => original.manufacturer
    }),
    columnHelper.accessor(row => row, {
      id: "artEan",
      header: () => "Art / EAN #",
      size: 220,
      cell: ({
        row: {
          original: { tyre_team_system_number, ean }
        }
      }) => (
        <>
          {tyre_team_system_number}
          {tyre_team_system_number && " / "}
          {ean && <Popup hoverable trigger={<span className="ean">{ean}</span>} content={ean} />}
        </>
      )
    }),
    columnHelper.accessor(row => row, {
      id: "gross",
      header: () => `${t("v8_gross", "Gross")} €`,
      size: 110,
      cell: ({ row: { original } }) => getGrossPrice(original)
    }),
    columnHelper.accessor(row => row, {
      id: "neto",
      header: () => `${t("v8_neto", "Net")} €`,
      size: 110,
      cell: ({ row: { original } }) => (
        <span className={original.price && original.price < (original.net_price ?? 0) ? "lower-customer-price" : ""}>{getNetPrice(original)}</span>
      )
    }),
    columnHelper.accessor(row => row, {
      id: "customer",
      header: () => `${t("v8_customer", "Customer")} € ${showPriceInVat ? t("v8_including_VAT", "including VAT") : t("v8_excluding_VAT", "excluding VAT")}`,
      size: 150,
      cell: ({ row: { original } }) => (original.price ? formatPrice(roundTwoDecimals(getDisplayedOfferPrice(original.price))) : null)
    }),
    columnHelper.accessor(row => row, {
      id: "load",
      header: () => t("v8_load", "Load"),
      size: 60,
      cell: ({ row: { original } }) => original.carry_capacity
    }),
    columnHelper.accessor(row => row, {
      id: "speed",
      header: () => <div className="th-speed">{t("v8_speed", "Speed")}</div>,
      size: 60,
      cell: ({ row: { original } }) => original.speed
    }),
    columnHelper.accessor(row => row, {
      id: "runflat",
      header: () => <div className="th-runflat">{t("v8_runflat", "Runflat")}</div>,
      size: 100,
      cell: ({ row: { original } }) => (original.run_flat_tire ? <Icon name="check circle" color="green" /> : null)
    }),
    columnHelper.accessor(row => row, {
      id: "channel",
      header: () => t("v8_channel", "Channel"),
      size: 110,
      cell: ({ row: { original } }) => getDeliveryChannelName(original.tyre_team_channel)
    }),
    columnHelper.accessor(row => row, {
      id: "stock",
      header: () => <div className="th-stock">{t("v8_stock", "Stock")}</div>,
      size: 80,
      cell: ({ row: { original } }) => original.stock
    }),
    columnHelper.accessor(row => row, {
      id: "delivery",
      header: () => t("v8_delivery", "Delivery"),
      size: 160,
      cell: ({ row: { original } }) => renderDeliveryPopup(original.tyre_team_delivery_method)
    }),
    columnHelper.accessor(row => row, {
      id: "add",
      header: () => <div className="th-add">{t("v8_add", "Add")}</div>,
      size: 75,
      cell: ({ row: { original } }) => (
        <Checkbox className="check" checked={original.selected} onChange={(_e, data) => onSelectionUpdate(original, data.checked ?? false)} />
      )
    })
  ];

  return (
    <div className="TyreExternalOfferTable">
      <ReactTable data={replacementOffers} columns={columns} />
    </div>
  );
};
